import { useState } from 'react'
import { cn } from '../../lib/utils'
import { TrendingUp, TrendingDown, Search, MessageSquare, ChevronDown } from 'lucide-react'
import { formatCurrency, formatPercent } from '../../config/chartTheme'
import { AiCommentary } from '../ai'

interface VarianceItem {
  metric_code: string
  metric_name: string
  current_value: number
  prior_value: number
  variance_amount: number
  variance_pct: number
  direction: 'FAVORABLE' | 'UNFAVORABLE'
  significance?: number  // 0-10
  commentary?: string
}

interface VarianceListProps {
  variances?: VarianceItem[]
  onDrill?: (metricCode: string) => void
  onAsk?: (question: string) => void
  loading?: boolean
  className?: string
}

export function VarianceList({
  variances = [], onDrill, onAsk, loading, className,
}: VarianceListProps) {
  const [expandedVariance, setExpandedVariance] = useState<string | null>(null)
  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 rounded-lg bg-white/[0.04] animate-pulse" />
        ))}
      </div>
    )
  }

  if (variances.length === 0) {
    return (
      <div className={cn(
        'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))] p-8 text-center',
        className,
      )}>
        <p className="text-[13px] text-[var(--text-muted,#64748B)]">
          No significant variances detected for this period.
        </p>
      </div>
    )
  }

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header */}
      <div className="flex items-center gap-1.5 mb-3">
        <div className="h-1.5 w-1.5 rounded-full bg-[var(--accent,var(--accent))]" />
        <span className="text-[10px] uppercase tracking-[0.8px] text-[var(--text-muted,#64748B)]">
          Significant Variances
        </span>
      </div>

      {variances.map((v) => {
        const isFav = v.direction === 'FAVORABLE'
        const significance = Math.min(v.significance || 5, 10)

        return (
          <div
            key={v.metric_code}
            className={cn(
              'rounded-lg border border-white/[0.06] bg-[var(--bg-surface,var(--bg-surface))]',
              'p-4 transition-colors hover:bg-white/[0.02]',
            )}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                {isFav ? (
                  <TrendingUp className="h-4 w-4 text-emerald-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
                <span className="text-[13px] font-medium text-[var(--text-primary,#F0F2F5)]">
                  {v.metric_name}
                </span>
                <span className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wide font-medium',
                  isFav
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : 'bg-red-500/10 text-red-400',
                )}>
                  {v.direction}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <span className={cn(
                  "font-['JetBrains_Mono',monospace] text-[13px] font-semibold",
                  isFav ? 'text-emerald-400' : 'text-red-400',
                )}>
                  {v.variance_amount >= 0 ? '+' : ''}{formatCurrency(v.variance_amount)}
                </span>
                <span className="text-[11px] text-[var(--text-muted,#64748B)] ml-1">
                  ({formatPercent(v.variance_pct)})
                </span>
              </div>
            </div>

            {/* Significance bar */}
            <div className="flex gap-0.5 mb-2">
              {Array.from({ length: 10 }).map((_, i) => (
                <div
                  key={i}
                  className={cn(
                    'h-1 flex-1 rounded-full',
                    i < significance
                      ? isFav ? 'bg-emerald-500/60' : 'bg-red-500/60'
                      : 'bg-white/[0.06]',
                  )}
                />
              ))}
            </div>

            {/* Commentary + actions */}
            <div className="flex items-center justify-between">
              {v.commentary && (
                <span className="text-[11px] text-[var(--text-muted,#64748B)] truncate flex-1 mr-4">
                  {v.commentary}
                </span>
              )}
              <div className="flex items-center gap-3 shrink-0">
                <button
                  onClick={() => setExpandedVariance(
                    expandedVariance === v.metric_code ? null : v.metric_code
                  )}
                  className="flex items-center gap-1 text-[11px] text-[var(--accent,var(--accent))] hover:underline"
                >
                  <ChevronDown className={cn(
                    'h-3 w-3 transition-transform duration-200',
                    expandedVariance === v.metric_code && 'rotate-180',
                  )} />
                  {expandedVariance === v.metric_code ? 'Hide' : 'AI Insight'}
                </button>
                {onDrill && (
                  <button
                    onClick={() => onDrill(v.metric_code)}
                    className="flex items-center gap-1 text-[11px] text-[var(--accent,var(--accent))] hover:underline"
                  >
                    <Search className="h-3 w-3" /> Drill Into
                  </button>
                )}
                {onAsk && (
                  <button
                    onClick={() => onAsk(`Why did ${v.metric_name} change by ${formatPercent(v.variance_pct)}?`)}
                    className="flex items-center gap-1 text-[11px] text-[var(--text-muted,#64748B)] hover:text-[var(--accent,var(--accent))]"
                  >
                    <MessageSquare className="h-3 w-3" /> Ask AI
                  </button>
                )}
              </div>
            </div>

            {/* Expandable AI Commentary */}
            {expandedVariance === v.metric_code && (
              <div className="mt-3 pt-3 border-t border-white/[0.06]">
                <AiCommentary metricCode={v.metric_code} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
